import React, { useEffect, useState } from 'react';
import { Layers3, Trash2, Plus, LogIn, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSpin } from '../../context/SpinContext';
import { listPresets, createPreset, deletePreset } from '../../lib/presetsApi';
import { Input } from '../ui/input';
import { toast } from '../ui/sonner';

export default function PresetsControls() {
  const { user, loading, login, logout } = useAuth();
  const { disc, background, exportSettings, updateDisc, updateBackground, updateExport } = useSpin();
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) { setPresets([]); return; }
    setPresetsLoading(true);
    listPresets().then(setPresets).catch(() => {}).finally(() => setPresetsLoading(false));
  }, [user]);

  const handleSave = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const settings = {
        disc: {
          designMode: disc.designMode, shape: disc.shape, size: disc.size,
          rpm: disc.rpm, reverse: disc.reverse, zoom: disc.zoom,
          spindleVisible: disc.spindleVisible, vinyl: disc.vinyl, centerHole: disc.centerHole,
        },
        background: { type: background.type, color: background.color },
        aspect: exportSettings.aspect,
      };
      const saved = await createPreset(newName.trim(), settings);
      setPresets((p) => [saved, ...p]);
      setNewName('');
      toast.success('Preset saved');
    } catch (e) {
      toast.error(e.message || 'Could not save preset.');
    } finally {
      setSaving(false);
    }
  };

  const handleApply = (preset) => {
    const s = preset.settings || {};
    if (s.disc) updateDisc(s.disc);
    if (s.background && s.background.type === 'color') updateBackground(s.background);
    if (s.aspect) updateExport({ aspect: s.aspect });
    toast.success(`Applied "${preset.name}"`);
  };

  const handleDelete = async (id) => {
    try {
      await deletePreset(id);
      setPresets((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e.message || 'Could not delete preset.');
    }
  };

  if (loading) return null;

  if (!user) {
    return (
      <div className="space-y-2.5">
        <p className="text-xs text-neutral-500 leading-relaxed">
          Sign in to save your disc settings as presets and reuse them next time.
        </p>
        <button
          type="button"
          onClick={login}
          className="flex items-center justify-center gap-2 w-full h-9 rounded-lg border border-neutral-200 hover:bg-neutral-50 text-sm font-medium text-neutral-700 transition-colors"
          data-testid="google-signin-button"
        >
          <LogIn className="w-3.5 h-3.5" /> Sign in with Google
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-6 h-6 rounded-full flex-shrink-0" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-neutral-200 flex-shrink-0" />
          )}
          <span className="text-xs text-neutral-600 truncate" data-testid="signed-in-user-email">{user.email}</span>
        </div>
        <button
          type="button"
          onClick={logout}
          className="flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-700 transition-colors flex-shrink-0"
          data-testid="sign-out-button"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Preset name"
          className="h-9 text-sm"
          data-testid="preset-name-input"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={!newName.trim() || saving}
          className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-neutral-900 hover:bg-neutral-800 disabled:bg-neutral-200 disabled:text-neutral-400 text-white text-sm font-medium transition-colors flex-shrink-0"
          data-testid="save-preset-button"
        >
          <Plus className="w-3.5 h-3.5" /> Save
        </button>
      </div>

      {presetsLoading ? (
        <p className="text-xs text-neutral-400">Loading presets…</p>
      ) : presets.length === 0 ? (
        <p className="text-xs text-neutral-400" data-testid="no-presets-message">No saved presets yet.</p>
      ) : (
        <div className="space-y-1.5" data-testid="preset-list">
          {presets.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200">
              <button
                type="button"
                onClick={() => handleApply(p)}
                className="flex items-center gap-2 text-sm text-neutral-700 hover:text-orange-700 truncate flex-1 text-left"
                data-testid={`apply-preset-${p.id}`}
              >
                <Layers3 className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{p.name}</span>
              </button>
              <button
                type="button"
                onClick={() => handleDelete(p.id)}
                className="text-neutral-400 hover:text-red-600 flex-shrink-0"
                data-testid={`delete-preset-${p.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
